import Link from 'next/link';
import { useEffect, useRef } from 'react';
import { splunkInstance } from '@/common/services/splunk';
import { buildSubstituteDoctorSearchText } from '@/common/utils/buildSubstituteDoctorSearch';

interface OnlineVisitSuggestionBannerProps {
  expertises: any;
  doctorCity?: string;
  slug: string;
}

/**
 * Shown under the booking card when the doctor has no bookable channel at all — in-person or
 * online — replacing the old named substitute-doctor suggestion there for the flagged population
 * (owner rollout, 2026-10-17, phase 1). Deliberately carries no other doctor's name here: the
 * owner asked that this doctor never be made to look replaced on their own profile. Naming the
 * substitute is deferred to /online-visit-suggestion, one click away.
 *
 * View + click events both fire here (not just click) so the owner can later compute a real
 * click-through rate, not just a raw click count with an unknown denominator.
 */
const OnlineVisitSuggestionBanner = ({ expertises, doctorCity, slug }: OnlineVisitSuggestionBannerProps) => {
  const groupName = expertises?.group_expertises?.[0]?.name;
  const text = buildSubstituteDoctorSearchText(expertises?.expertises);
  const href = `/online-visit-suggestion/?${new URLSearchParams({
    text,
    city: doctorCity || 'ir',
    ...(groupName ? { group: groupName } : {}),
    from: slug,
  }).toString()}`;

  const viewSentFor = useRef<string | null>(null);
  useEffect(() => {
    if (!slug || viewSentFor.current === slug) return;
    viewSentFor.current = slug;
    splunkInstance('search').sendEvent({
      group: 'profile_visit_online_doctor_recommendation',
      type: 'profile_visit_online_doctor_recommendation-view-banner',
      event: { slug, variant: 'no-online-visit-banner' },
    });
  }, [slug]);

  const handleClick = () => {
    splunkInstance('search').sendEvent({
      group: 'profile_visit_online_doctor_recommendation',
      type: 'profile_visit_online_doctor_recommendation-click-banner',
      event: { slug, variant: 'no-online-visit-banner' },
    });
  };

  return (
    <Link
      href={href}
      onClick={handleClick}
      className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-3.5 py-3 hover:border-primary/40"
    >
      <span className="flex items-center justify-center w-8 h-8 flex-shrink-0 rounded-full bg-primary/10 text-primary text-sm">
        💬
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-800">
          {groupName ? `یک متخصص ${groupName} دیگر` : 'یک متخصص دیگر'} همین الان آنلاین پاسخگوست
        </p>
        <p className="text-xs text-slate-500 mt-0.5">بدون نوبت، بدون رفت‌وآمد - اگر عجله دارید</p>
      </div>
      <span className="text-primary text-sm font-medium whitespace-nowrap">مشاهده</span>
    </Link>
  );
};

export default OnlineVisitSuggestionBanner;
